import { useState, useEffect } from 'react';
import { create } from 'zustand';
import { useGetAccountDetailInfoQuery, GetAccountDetailInfoQuery } from 'views/utils/graphql/graphql';
import {
  CostomerInfoType,
  DepositAccountType,
  finedFinancialAssetsStr,
  finedAnnualIncomeStr,
  finedPolicyStr,
  EmploymentType,
  createPurposesInfoList,
  createIncomeFormInfoList,
  InvestmentInfoType,
  finedNationalityValue,
  finedListedCompanyPostStr,
  finedPofessionStr,
  createExperiencesInfoList,
  scrollToTop,
} from 'views/utils/accountEstablish';
import { ButtonProps, ButtonSize, ButtonType, FillType } from 'views/components/atoms/button';
import { SearchSelectProps } from 'views/components/atoms/searchSelect';
import { useCostomerInfoUpdate } from './useCustomerInfoUpdate';
import { useDepositAccountUpdate } from './useDepositAccountUpdate';
import { useEmploymentUpdate } from './useEmploymentUpdate';
import { useInvestmentInfoUpdate } from './useInvestmentInfoUpdate';
import { PageStep } from './index';
import { isEmpty } from 'lodash';
import { useGlobalLoading, useGlobalLoadingHandler } from 'views/components/organisms/globalLoading/hook';

export enum SelectItem {
  CustomerInfo = 'お客様情報',
  DepositAccount = '指定預金口座情報',
  Employment = 'お勤め先情報',
  InvestmentInfo = '投資情報',
}
const SelectList: SelectItem[] = (Object.keys(SelectItem) as (keyof typeof SelectItem)[]).map((key) => {
  return SelectItem[key];
});

//更新ページ
export const useUpdatePage = (
  pageStep: PageStep,
  setPageStep: React.Dispatch<React.SetStateAction<PageStep>>,
  newPhoneNumber: string,
  setNewPhoneNumber: React.Dispatch<React.SetStateAction<string>>,
  onSendSMS: () => Promise<void>,
) => {
  const [selected, setSelected] = useState<SelectItem>(SelectItem.CustomerInfo);
  const { add, initialize } = useAccountInfoUpdateStore();
  const { showLoading, hideLoading } = useGlobalLoadingHandler();
  const { isShow, isCenter } = useGlobalLoading();

  const [accountDetail, getAccountDetail] = useGetAccountDetailInfoQuery({
    variables: {},
    requestPolicy: 'network-only',
    pause: true,
  });

  const costomerInfoProps = useCostomerInfoUpdate();
  const depositAccountProps = useDepositAccountUpdate();
  const employmentProps = useEmploymentUpdate();
  const investmentInfoProps = useInvestmentInfoUpdate();

  useEffect(() => {
    scrollToTop();
  }, [selected, pageStep]);

  // SMS認証が終わってからアカウント情報を取得する
  // 初期時に取得するとSMS認証しなくてもネットワークを見れば個人情報が見れてしまうので
  useEffect(() => {
    if (pageStep === PageStep.UPDATE && !accountDetail.data) {
      getAccountDetail();
    }
  }, [pageStep]);

  useEffect(() => {
    if (accountDetail.fetching) {
      showLoading();
    } else {
      hideLoading();
    }
    if (!accountDetail.data) return;
    initialize(accountDetail.data as GetAccountDetailInfoQuery);
  }, [accountDetail]);

  useEffect(() => {
    if (
      costomerInfoProps.formik.values.mobilePhoneNumber !== accountDetail.data?.GetAccountDetailInfo?.MobilePhoneNumber
    ) {
      setNewPhoneNumber(costomerInfoProps.formik.values.mobilePhoneNumber);
    }
  }, [costomerInfoProps.formik.values.mobilePhoneNumber]);

  const onSubmit = async (selected: SelectItem) => {
    if (selected === SelectItem.CustomerInfo) {
      const valid = await costomerInfoProps.formik.validateForm(costomerInfoProps.formik.values);
      if (isEmpty(valid)) {
        costomerInfoProps.formik.handleSubmit();
        return true;
      }
      return false;
    }
    if (selected === SelectItem.DepositAccount) {
      const valid = await depositAccountProps.formik.validateForm(depositAccountProps.formik.values);
      if (isEmpty(valid)) {
        depositAccountProps.formik.handleSubmit();
        return true;
      }
      return false;
    }
    if (selected === SelectItem.Employment) {
      const valid = await employmentProps.formik.validateForm(employmentProps.formik.values);
      if (isEmpty(valid)) {
        employmentProps.formik.handleSubmit();
        return true;
      }
      return false;
    }
    if (selected === SelectItem.InvestmentInfo) {
      const valid = await investmentInfoProps.formik.validateForm(investmentInfoProps.formik.values);
      if (isEmpty(valid)) {
        investmentInfoProps.formik.handleSubmit();
        return true;
      }
      return false;
    }
  };

  const selectProps: SearchSelectProps = {
    selectedOption: selected,
    setSelectedOption: async (select) => {
      const valid = await onSubmit(selected as SelectItem);
      if (valid) {
        setSelected(select as SelectItem);
      }
    },
    options: SelectList,
  };

  const executeConfirm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const valid = await onSubmit(selected as SelectItem);
    if (valid) {
      newPhoneNumber === '' ? setPageStep(PageStep.CONFIRM) : onSendSMS();
    }
  };

  const updateBtnProps: ButtonProps = {
    label: '更新内容を確認する',
    type: ButtonType.Submit,
    fillType: FillType.primaryFill,
    buttonSize: ButtonSize.sizeM,
    // onClickイベントははform.onSubmitで呼び出すようにする
    // (formベースにしないとminlengthなどのhtmlバリデーションが効かないため)
    onClick: () => {},
  };

  return {
    selectProps,
    updateBtnProps,
    costomerInfoProps,
    depositAccountProps,
    employmentProps,
    investmentInfoProps,
    setSelected,
    executeConfirm,
  };
};

//入力データ管理
export type AccountInfoUpdateRequestParam = CostomerInfoType & DepositAccountType & EmploymentType & InvestmentInfoType;

export type AccountInfoUpdateStore = {
  param: AccountInfoUpdateRequestParam;
  add: (data: AccountInfoUpdateRequestParam) => void;
  initialize: (param: GetAccountDetailInfoQuery) => void;
};
export const useAccountInfoUpdateStore = create<AccountInfoUpdateStore>((set) => ({
  param: {} as AccountInfoUpdateRequestParam,
  add: (data: AccountInfoUpdateRequestParam) => set((state) => ({ param: { ...state.param, ...data } })),
  initialize: (param: GetAccountDetailInfoQuery) => {
    const isYuchoBank = param.GetAccountDetailInfo?.BankCode === '9900';
    set(() => ({
      param: {
        //お客様情報
        nationality: finedNationalityValue(Number(param.GetAccountDetailInfo?.Nationality)) ?? '',
        americanOnTaxSystem: !!param.GetAccountDetailInfo?.AmericanOnTaxSystem,
        ssn: param.GetAccountDetailInfo?.SSN ?? null,
        itin: param.GetAccountDetailInfo?.ITIN ?? null,
        fatcaAgreement: param.GetAccountDetailInfo?.FATCAAgreement ?? null,
        fatcaOath: param.GetAccountDetailInfo?.FATCAOath ?? null,
        firstName: param.GetAccountDetailInfo?.FirstName ?? '',
        lastName: param.GetAccountDetailInfo?.LastName ?? '',
        firstNameKana: param.GetAccountDetailInfo?.FirstNameKana ?? '',
        lastNameKana: param.GetAccountDetailInfo?.LastNameKana ?? '',
        birthday: param.GetAccountDetailInfo?.Birthday ?? '',
        sex: param.GetAccountDetailInfo?.Sex ?? null,
        postCode: param.GetAccountDetailInfo?.PostCode ?? '',
        prefecture: param.GetAccountDetailInfo?.Prefecture ?? '',
        cityCounty: param.GetAccountDetailInfo?.CityCounty ?? '',
        townStreetAddress: param.GetAccountDetailInfo?.TownStreetAddress ?? null,
        buildingNameRoomNumber: param.GetAccountDetailInfo?.BuildingNameRoomNumber ?? null,
        isNoBuildingNameRoomNumber: !param.GetAccountDetailInfo?.BuildingNameRoomNumber,
        mobilePhoneNumber: param.GetAccountDetailInfo?.MobilePhoneNumber ?? '',
        homePhoneNumber: param.GetAccountDetailInfo?.HomePhoneNumber ?? null,
        mailAddress: param.GetAccountDetailInfo?.MailAddress ?? '',
        //指定預金口座情報
        isYuchoBank: isYuchoBank,
        bankCode: isYuchoBank ? '' : param.GetAccountDetailInfo?.BankCode ?? '',
        bankName: '', //あとでセットする
        branchCode: isYuchoBank ? '' : param.GetAccountDetailInfo?.BranchCode ?? '',
        branchName: '', //あとでセットする
        accountNumber: isYuchoBank ? '' : param.GetAccountDetailInfo?.AccountNumber ?? '',
        depositForm: isYuchoBank ? 0 : Number(param.GetAccountDetailInfo?.DepositForm),
        bankCodeOfYucho: isYuchoBank ? param.GetAccountDetailInfo?.BankCode ?? '' : '',
        bankNameOfYucho: '', //あとでセットする
        branchCodeOfYucho: isYuchoBank ? param.GetAccountDetailInfo?.BranchCode ?? '' : '',
        branchNameOfYucho: '', //あとでセットする
        accountNumberOfYucho: isYuchoBank ? param.GetAccountDetailInfo?.AccountNumber ?? '' : '',
        depositFormOfYucho: isYuchoBank ? Number(param.GetAccountDetailInfo?.DepositForm) : 0,
        //お勤め先情報
        profession: finedPofessionStr(Number(param.GetAccountDetailInfo?.Profession)) ?? '',
        workplace: param.GetAccountDetailInfo?.Workplace ?? '',
        isNoWorkplace: !param.GetAccountDetailInfo?.Workplace,
        department: param.GetAccountDetailInfo?.Department ?? '',
        isNoDepartment: !param.GetAccountDetailInfo?.Department,
        post: param.GetAccountDetailInfo?.Post ?? '',
        isNoPost: !param.GetAccountDetailInfo?.Post,
        workPhoneNumber: param.GetAccountDetailInfo?.WorkPhoneNumber ?? '',
        isNoWorkPhoneNumber: !param.GetAccountDetailInfo?.WorkPhoneNumber,
        listedCompanyCheck: !!param.GetAccountDetailInfo?.ListedCompanyCheck,
        listedCompanyPost: finedListedCompanyPostStr(Number(param.GetAccountDetailInfo?.ListedCompanyPost)) ?? null,
        listedCompanyName: param.GetAccountDetailInfo?.ListedCompanyName ?? null,
        outSidePost: param.GetAccountDetailInfo?.OutSidePost ?? null,
        retirementDate: param.GetAccountDetailInfo?.RetirementDate ?? null,
        insiderBoardMemberFirstName: param.GetAccountDetailInfo?.InsiderBoardMemberFirstName ?? null,
        insiderBoardMemberLastName: param.GetAccountDetailInfo?.InsiderBoardMemberLastName ?? null,
        //投資情報
        investmentExperiences: createExperiencesInfoList(param.GetAccountDetailInfo?.InvestmentExperiences as number[]),
        investmentPolicy: finedPolicyStr(Number(param.GetAccountDetailInfo?.InvestmentPolicy)) ?? '',
        investmentPurposes: createPurposesInfoList(param.GetAccountDetailInfo?.InvestmentPurposes as number[]),
        annualIncome: finedAnnualIncomeStr(Number(param.GetAccountDetailInfo?.AnnualIncome)) ?? '',
        incomeForm: createIncomeFormInfoList(param.GetAccountDetailInfo?.IncomeForm as number[]),
        financialAssets: finedFinancialAssetsStr(Number(param.GetAccountDetailInfo?.FinancialAssets)) ?? '',
      },
    }));
  },
}));
